import { useCallback, useRef, useEffect, useState } from 'preact/hooks';
import Utils, { IS_EYEJACK_APP } from '@webcore/core/utils/utils';
import useLocalStorage from '@core/hooks/useLocalStorage';
import useUserDevice from '@core/hooks/useUserDevice';
import * as Sentry from "@sentry/react";

// ============================================================================
// TYPES
// ============================================================================

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export interface XRAnalyticsConfig {
  measurementId: string;
  autoSendPageView?: boolean;
  debug?: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const USER_ID_KEY = 'ejx_user_id';
const SESSION_ID_KEY = 'ejx_session_id';

// ============================================================================
// SESSION ID
// ============================================================================

function getJourneyId(): string {
  const url = new URL(window.location.href);
  const urlSessionId = url.searchParams.get('sessionId');
  
  if (IS_EYEJACK_APP) {
    // App Clip: use URL param from website handoff
    return urlSessionId || Utils.UUIDShort();
  }
  
  // Website: sessionStorage is source of truth (ignore URL params)
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = Utils.UUIDShort();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * XR Analytics hook with cross-context tracking for Safari → App Clip journeys.
 * 
 * Features:
 * - Persistent userId across website and App Clip
 * - Journey tracking (links Safari + App Clip as one session)
 * - Device/platform context (ios, android, appclip, macos, windows, linux)
 * - GA4 integration
 */
export function useXRAnalyticsSession(config: XRAnalyticsConfig) {
  const { measurementId, autoSendPageView = false, debug = false } = config;
  
  const isInitialized = useRef(false);
  const { device } = useUserDevice();
  
  // Async localStorage (handles native App Clip storage)
  const [storedUserId, setStoredUserId, , isStorageLoading] = useLocalStorage<string | null>(USER_ID_KEY, null);
  
  const [userId, setUserId] = useState<string | null>(null);
  const [isUserIdLoading, setIsUserIdLoading] = useState(true);
  const [journeyId] = useState(() => getJourneyId());
  
  // Map device to analytics context
  const context = (() => {
    switch (device) {
      case 'AppClip': return 'appclip';
      case 'iOS': return 'ios';
      case 'Android': return 'android';
      case 'Mac OS': return 'macos';
      case 'Windows': return 'windows';
      case 'Linux': return 'linux';
      default: return 'unknown';
    }
  })();

  const log = useCallback((...args: any[]) => {
    if (debug) console.log('[XRAnalytics]', ...args);
  }, [debug]);

  // Resolve userId once storage is loaded
  useEffect(() => {
    if (isStorageLoading || !device) return;
    
    if (storedUserId) {
      setUserId(storedUserId);
      setIsUserIdLoading(false);
      return;
    }
    
    // No stored userId
    if (device === 'AppClip') {
      // Try URL param (handoff from website)
      const urlUserId = new URL(window.location.href).searchParams.get('userId');
      if (urlUserId) {
        setStoredUserId(urlUserId);
        setUserId(urlUserId);
      } else {
        const newId = Utils.UUIDShort();
        setStoredUserId(newId);
        setUserId(newId);
      }
    } else {
      // Website: generate new (ignore URL params - could be shared link)
      const newId = Utils.UUIDShort();
      setStoredUserId(newId);
      setUserId(newId);
    }
    setIsUserIdLoading(false);
  }, [isStorageLoading, storedUserId, device, setStoredUserId]);

  // Sync IDs to URL for App Clip handoff (website only)
  useEffect(() => {
    if (IS_EYEJACK_APP || !userId) return;
    
    const url = new URL(window.location.href);
    let needsUpdate = false;
    
    if (url.searchParams.get('userId') !== userId) {
      url.searchParams.set('userId', userId);
      needsUpdate = true;
    }
    if (url.searchParams.get('sessionId') !== journeyId) {
      url.searchParams.set('sessionId', journeyId);
      needsUpdate = true;
    }
    
    if (needsUpdate) {
      window.history.replaceState(null, '', url.toString());
    }
  }, [userId, journeyId]);

  const initAnalytics = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      if (isInitialized.current) return resolve();
      if (!userId) return reject(new Error('userId not ready'));

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.onerror = () => reject(new Error('Failed to load GA script'));
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      function gtag(..._args: any[]) { window.dataLayer.push(arguments); }
      window.gtag = gtag;

      script.onload = () => {
        window.gtag('js', new Date());
        window.gtag('config', measurementId, {
          user_id: userId,
          send_page_view: autoSendPageView,
          user_properties: { context, journey_id: journeyId },
        });
        isInitialized.current = true;
        log('Initialized:', { measurementId, userId, journey_id: journeyId, context });
        resolve();
      };
    });
  }, [measurementId, userId, journeyId, context, autoSendPageView, log]);

  const sendEvent = useCallback((eventName: string, params: Record<string, any> = {}) => {
    if (typeof window.gtag !== 'function') {
      log('Not initialized, event dropped:', eventName);
      return;
    }

    try {
      const fullParams = {
        ...params,
        user_id: userId,
        journey_id: journeyId,
        context,
      };
      window.gtag('event', eventName, fullParams);
      log('Event:', eventName, fullParams);
    } catch (error) {
      console.error(`[XRAnalytics] Event failed: ${eventName}`, error);
      Sentry.captureException(error);
    }
  }, [userId, journeyId, context, log]);

  const trackPageView = useCallback((pagePath?: string, pageTitle?: string) => {
    sendEvent('page_view', {
      page_path: pagePath || window.location.pathname,
      page_title: pageTitle || document.title,
    });
  }, [sendEvent]);

  return {
    initAnalytics,
    sendEvent,
    trackPageView,
    userId,
    journeyId,
    context,
    isUserIdLoading,
  };
}

// ============================================================================
// UTILITY - For building App Clip URLs
// ============================================================================

export function getCurrentUserId(): string {
  const storedId = localStorage.getItem(USER_ID_KEY);
  if (storedId) return storedId;
  
  const newId = Utils.UUIDShort();
  localStorage.setItem(USER_ID_KEY, newId);
  return newId;
}
